import { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);
const RESERVED_SEGMENTS = ['auth', 'app', 'admin', 'api', 'staff', 'preview', 'staff-portal', 'finance', 'deposit-return'];
const PLATFORM_DOMAIN = (process.env.REACT_APP_PLATFORM_DOMAIN || '').toLowerCase();

const deriveUsername = (email = '') => {
  const prefix = String(email || '').split('@')[0].toLowerCase().replace(/[^a-z0-9_]/g, '');
  return prefix || undefined;
};

const resolveTenantSlug = () => {
  const path = window.location.pathname;
  const match = path.match(/^\/([a-z0-9-]+)(\/|$)/i);
  if (match && !RESERVED_SEGMENTS.includes(match[1])) {
    return match[1];
  }
  const host = window.location.hostname || '';
  const parts = host.split('.').filter(Boolean);
  if (PLATFORM_DOMAIN && parts.length >= 3 && parts.slice(-2).join('.') === PLATFORM_DOMAIN) {
    const subdomain = parts[0].toLowerCase();
    if (!['www', 'api', 'admin'].includes(subdomain)) return subdomain;
  }
  return null;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantSlug, setTenantSlug] = useState(resolveTenantSlug());

  useEffect(() => {
    const resolved = resolveTenantSlug();
    if (resolved) setTenantSlug(resolved);
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (!token) { setUser(null); setLoading(false); return; }
      const res = await api.get('/api/auth/me');
      if (res.data?.tenant_slug) setTenantSlug(res.data.tenant_slug);
      setUser(res.data);
    } catch {
      localStorage.removeItem('session_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (identifier, password, tenant = null, portal = null) => {
    localStorage.removeItem('session_token');
    setUser(null);
    const res = await api.post('/api/auth/login', {
      identifier,
      password,
      tenant_slug: tenant || tenantSlug
    });
    const token = res.data.token || res.data.session_id;
    if (token) localStorage.setItem('session_token', token);
    if (res.data.user?.tenant_slug) setTenantSlug(res.data.user.tenant_slug);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, fullName, username = null, referralCode = null, tenant = null, emailOtp = null) => {
    localStorage.removeItem('session_token');
    setUser(null);
    const res = await api.post('/api/auth/signup', {
      email,
      password,
      username: username || deriveUsername(email),
      referral_code: referralCode || undefined,
      tenant_slug: tenant || tenantSlug || undefined,
      email_otp: emailOtp || undefined,
    });
    const token = res.data.token || res.data.session_id;
    if (token) localStorage.setItem('session_token', token);
    if (res.data.user?.tenant_slug) setTenantSlug(res.data.user.tenant_slug);
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    try { await api.post('/api/auth/logout'); } catch {}
    localStorage.removeItem('session_token');
    setUser(null);
  };

  const exchangeSession = async (sessionId) => {
    localStorage.setItem('session_token', sessionId);
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout,
      exchangeSession, checkAuth, tenantSlug, setTenantSlug
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
